/**
 * Публичные настройки сети: приветственный бонус, лимит списания баллов,
 * сроки оплаты и брони. Отдаются без токена — их видит и гость.
 *
 * Значения приходят строками: типизировать их по одному значило бы держать
 * копию серверного справочника. Экран разбирает ровно те, что показывает.
 */
import { useQuery } from '@tanstack/react-query'
import { formatPhone } from '@dorixona/utils'
import { useApi } from '../api/context'
import { keys } from '../api/keys'

export type PublicSettings = Record<string, string>

/** Число из настройки; неизвестный ключ или мусор — запасное значение. */
export function num(settings: PublicSettings | undefined, key: string, fallback: number): number {
  const value = Number(settings?.[key])
  return Number.isFinite(value) ? value : fallback
}

/** Переключатель: сервер пишет их строками «true» и «false». */
export function flag(settings: PublicSettings | undefined, key: string): boolean {
  return settings?.[key] === 'true'
}

export function useSettings() {
  const api = useApi()
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api.get<PublicSettings>('/settings/public'),
  })
}

/** Интеграция «Карты и адреса» в админке: `off` — Яндекс Карт и геокодера нет. */
export const GEO_PROVIDER = 'integration.geo.provider'

/**
 * Включены ли Яндекс Карты и геокодер. Пока настройки идут — `null`: карту не
 * грузим и «выключено» не показываем. Настройки не пришли — карты включены:
 * сбой одного запроса не должен отнимать у покупателя карту.
 */
export function useMapsOn(): boolean | null {
  const settings = useSettings()
  if (settings.isPending) return null
  return settings.data?.[GEO_PROVIDER] !== 'off'
}

/** Телефон поддержки: админка, «Настройки → Организация». */
export const SUPPORT_PHONE = 'org.support_phone'

export interface SupportPhone {
  /** Как показать: «+998 71 200-00-01»; номер не по маске — как его ввели. */
  text: string
  /** Ссылка для звонка: `tel:+998712000001`. */
  href: string
}

/**
 * Телефон поддержки из публичных настроек — его видит и гость. Пусто, нет
 * цифр или настройки не пришли — null: строку с телефоном не показываем.
 */
export function supportPhone(settings: PublicSettings | undefined): SupportPhone | null {
  const raw = (settings?.[SUPPORT_PHONE] ?? '').trim()
  const digits = raw.replace(/\D/g, '')
  if (!digits) return null
  const plus = raw.startsWith('+') || digits.startsWith('998')
  return { text: formatPhone(raw), href: `tel:${plus ? '+' : ''}${digits}` }
}

export function useSupportPhone(): SupportPhone | null {
  return supportPhone(useSettings().data)
}
